/**
 * 搜索过滤栏组件
 *
 * @experimental 此组件尚未在消费项目中广泛使用，API 可能调整。
 *
 * 配置驱动的搜索过滤栏，支持多种字段类型:
 * - input: 文本输入
 * - select: 下拉选择
 * - dateRange: 日期范围
 * - date: 单日期
 * - number: 数字输入
 * - custom: 自定义渲染
 *
 * 使用 antd Form + 标准输入组件，确保与 antd 主题一致。
 */

import { DownOutlined, SearchOutlined, UpOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
} from 'antd';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { CustomFieldAdapter } from '../CustomFieldAdapter';

// ===== 类型定义 =====

/**
 * 搜索字段类型
 */
export type SearchFieldType =
  | 'input'
  | 'select'
  | 'dateRange'
  | 'date'
  | 'number'
  | 'custom';

/**
 * 选项
 */
interface FieldOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

/**
 * 搜索字段配置
 */
export interface SearchField {
  /** 字段名 (对应表单 name) */
  name: string;
  /** 显示标签 */
  label?: string;
  /** 字段类型 */
  type: SearchFieldType;
  /** 占位符 */
  placeholder?: string;
  /** 下拉选项 (type='select') */
  options?: FieldOption[];
  /** 默认值 */
  defaultValue?: unknown;
  /** 栅格列宽 (24分制), 默认 6 */
  span?: number;
  /** 自定义渲染 (type='custom') */
  render?: (value: unknown, onChange: (v: unknown) => void) => React.ReactNode;
  /** 输入后是否立即触发搜索 (默认 false, 需点搜索按钮) */
  immediate?: boolean;
}

/**
 * SearchFilterBar 属性
 */
export interface SearchFilterBarProps {
  /** 搜索字段配置 */
  fields: SearchField[];
  /** 搜索回调 */
  onSearch: (values: Record<string, unknown>) => void;
  /** 重置回调 */
  onReset?: () => void;
  /** 是否显示重置按钮, 默认 true */
  showReset?: boolean;
  /** 搜索按钮文字, 默认 '搜索' */
  searchText?: string;
  /** 重置按钮文字, 默认 '重置' */
  resetText?: string;
  /** 展开按钮文字, 默认 '展开' */
  expandText?: string;
  /** 折叠按钮文字, 默认 '收起' */
  collapseText?: string;
  /** 是否默认折叠 (字段数 > collapseThreshold 时自动折叠), 默认 false */
  defaultCollapsed?: boolean;
  /** 折叠阈值 (超过此数量的字段默认隐藏), 默认 3 */
  collapseThreshold?: number;
  /** 自定义类名 */
  className?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 额外的操作按钮区域 */
  extra?: React.ReactNode;
}

function pickNonEmptyValues(
  values: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== null && value !== '') {
      result[key] = value;
    }
  }
  return result;
}

// ===== 组件实现 =====

/**
 * 搜索过滤栏组件
 *
 * @example
 * ```tsx
 * <SearchFilterBar
 *   fields={[
 *     { name: 'keyword', type: 'input', placeholder: '搜索用户名/邮箱...' },
 *     { name: 'status', type: 'select', label: '状态', options: [
 *       { label: '全部', value: '' },
 *       { label: '启用', value: 'active' },
 *       { label: '禁用', value: 'inactive' },
 *     ]},
 *     { name: 'dateRange', type: 'dateRange', label: '创建时间' },
 *   ]}
 *   onSearch={(values) => setSearchParams(values)}
 *   onReset={() => setSearchParams({})}
 * />
 * ```
 */
export function SearchFilterBar(props: SearchFilterBarProps) {
  const {
    fields,
    onSearch,
    onReset,
    showReset = true,
    searchText = '搜索',
    resetText = '重置',
    expandText = '展开',
    collapseText = '收起',
    defaultCollapsed = false,
    collapseThreshold = 3,
    className,
    style,
    extra,
  } = props;

  const [form] = Form.useForm();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  // 构建初始值
  const initialValues = useMemo(() => {
    const vals: Record<string, unknown> = {};
    for (const field of fields) {
      if (field.defaultValue !== undefined) {
        vals[field.name] = field.defaultValue;
      }
    }
    return vals;
  }, [fields]);

  const handleSearch = useCallback(() => {
    onSearch(pickNonEmptyValues(form.getFieldsValue()));
  }, [form, onSearch]);

  const handleReset = useCallback(() => {
    form.resetFields();
    onReset?.();
    // 发送默认值（而非空对象），保持 UI 和搜索状态一致
    onSearch(pickNonEmptyValues(form.getFieldsValue()));
  }, [form, onReset, onSearch]);

  // 记录哪些字段标记为 immediate
  const immediateFieldNames = useMemo(
    () => new Set(fields.filter((f) => f.immediate).map((f) => f.name)),
    [fields],
  );

  // Form.onValuesChange: 当 immediate 字段变化时自动触发搜索
  const handleValuesChange = useCallback(
    (changedValues: Record<string, unknown>) => {
      const changedKeys = Object.keys(changedValues);
      if (changedKeys.some((key) => immediateFieldNames.has(key))) {
        // 使用 Promise.resolve 延迟到当前 setState 批处理完成后再读取表单值
        Promise.resolve().then(handleSearch);
      }
    },
    [immediateFieldNames, handleSearch],
  );

  // 可见字段
  const visibleFields = collapsed ? fields.slice(0, collapseThreshold) : fields;
  const showCollapse = fields.length > collapseThreshold;

  return (
    <div className={className} style={style}>
      <Form
        form={form}
        initialValues={initialValues}
        onValuesChange={handleValuesChange}
      >
        <Row gutter={[16, 16]} align="middle">
          {visibleFields.map((field) => (
            <Col key={field.name} span={field.span ?? 6}>
              <Form.Item
                name={field.name}
                label={field.label}
                style={{ marginBottom: 0 }}
              >
                {renderSearchField(field, handleSearch)}
              </Form.Item>
            </Col>
          ))}

          {/* 操作按钮 */}
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
              >
                {searchText}
              </Button>
              {showReset && <Button onClick={handleReset}>{resetText}</Button>}
              {showCollapse && (
                <Button
                  type="link"
                  onClick={() => setCollapsed(!collapsed)}
                  icon={collapsed ? <DownOutlined /> : <UpOutlined />}
                >
                  {collapsed ? expandText : collapseText}
                </Button>
              )}
              {extra}
            </Space>
          </Col>
        </Row>
      </Form>
    </div>
  );
}

// ===== 字段渲染 =====

function renderSearchField(
  field: SearchField,
  onSearch: () => void,
): React.ReactNode {
  switch (field.type) {
    case 'input':
      return (
        <Input
          placeholder={field.placeholder ?? `请输入${field.label ?? ''}`}
          allowClear
          onPressEnter={onSearch}
        />
      );

    case 'number':
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder={field.placeholder ?? `请输入${field.label ?? ''}`}
        />
      );

    case 'select':
      return (
        <Select
          placeholder={field.placeholder ?? `请选择${field.label ?? ''}`}
          allowClear
          options={field.options?.map((opt) => ({
            label: opt.label,
            value: opt.value,
            disabled: opt.disabled,
          }))}
        />
      );

    case 'date':
      return <DatePicker style={{ width: '100%' }} />;

    case 'dateRange':
      return <DatePicker.RangePicker style={{ width: '100%' }} />;

    case 'custom':
      if (field.render) {
        return <CustomFieldAdapter render={field.render} />;
      }
      return null;

    default:
      return null;
  }
}
